import { IIconNames } from '@hai/ui-react';
import { TFunction } from 'i18next';
import { GenericResponse } from 'src/js/model/api/response/generic-response';
import { capitalizeTheFirstLetters } from 'src/js/util';
import { isIpv4OrEmpty, isNotIpv4Multicast } from 'src/js/validator/ip-validator-helper';
import { ipOrHostname, port } from 'src/js/validator/validator';
import * as yup from 'yup';

export enum St2110State {
  STOPPED = 0,
  RUNNING,
  FAILED,
}

export enum St2110Type {
  video = 'video',
  audio = 'audio',
  data = 'data',
}

export interface St2110Info {
  address: string;
  autoStart?: boolean;
  id: number;
  inputChannel: number;
  inputFormat?: string;
  name?: string;
  numOfChannels?: number;
  output: number; // 1 to 4
  packetTime?: number;
  packetTimeActual?: number;
  port: number;
  rtpPayload: number;
  sourceAddress?: string;
  state: St2110State;
  stateString: string;
  type: St2110Type;
}

export interface St2110Stats {
  detectedVideoFormat?: string;
  lastReset: string;
  txPackets: string;
  state: St2110State;
  stateString: string;
  streamSync: boolean;
  videoFormatDetectionState?: string;
}

export const st2110IsStopped = (state: St2110State): boolean =>
  state === St2110State.STOPPED || state === St2110State.FAILED;
export const st2110IsStarted = (state: St2110State): boolean =>
  state === St2110State.RUNNING || state === St2110State.FAILED;

export const st2110TypeToNoName = (type: string, t: TFunction) => {
  if (type === 'data') {
    return t('st2110.noNameData');
  }
  return capitalizeTheFirstLetters(type);
};

export const st2110TypeToString = (type: string, t: TFunction): string => {
  return t(`st2110.mediaTypes.${type}`);
};

export const st2110TypeToIcon = (type: string): IIconNames => {
  switch (type) {
    case 'audio':
      return 'AudioEncoder';
    case 'video':
      return 'VideoEncoder';
    case 'data':
      return 'AncillaryData';
  }
  return 'VideoEncoder';
  100;
};

export const st2110StateToIcon = (state: St2110State, streamSync: boolean): IIconNames => {
  switch (state) {
    case St2110State.STOPPED:
      return 'StatusStopped';
    case St2110State.RUNNING:
      return streamSync ? 'StatusOK' : 'StatusStreamWarning';
    case St2110State.FAILED:
      return 'StatusError';
  }
};

export const st2110StateToName = (state: St2110State, t: TFunction): string => {
  switch (state) {
    case St2110State.STOPPED:
      return t('st2110.states.stopped');
    case St2110State.RUNNING:
      return t('st2110.states.running');
    case St2110State.FAILED:
      return t('st2110.states.failed');
  }
};

const st2110StateToSortable = (state: St2110State, streamSync: boolean): number => {
  switch (state) {
    case St2110State.RUNNING:
      return streamSync ? 1 : 2;
    case St2110State.STOPPED:
      return 3;
    case St2110State.FAILED:
      return 4;
  }
};

export const st2110StateToColor = (state: St2110State, streamSync: boolean): string => {
  switch (state) {
    case St2110State.STOPPED:
      return 'haiui-gray-07';
    case St2110State.RUNNING:
      return streamSync ? 'haiui-green-01' : 'haiui-amber-01';
    case St2110State.FAILED:
      return 'haiui-red-01';
  }
};

export const st2110StateToStatus = (state: St2110State, streamSync: boolean): string => {
  switch (state) {
    case St2110State.STOPPED:
      return 'INACTIVE';
    case St2110State.RUNNING:
      return streamSync ? 'ACTIVE' : 'ALERT';
    case St2110State.FAILED:
      return 'FAILED';
  }
};

export const st2110ModelToStatus = (model: St2110ViewModel): string => {
  return st2110StateToStatus(model.state, model.stats.streamSync);
};

export interface St2110Response extends GenericResponse {
  info: St2110Info;
  stats: St2110Stats;
  warning?: string;
}

export interface GetAllSt2110sResponse {
  data: St2110Response[];
}

export interface St2110ViewModel extends St2110Info {
  stats?: St2110Stats;
  state: St2110State;

  stateSortable: number;
  addressTxt: string;
  typeTxt: string;

  status: string;

  selected: boolean /* checked by checkbox */;
  hidden: boolean /* when filtered out */;
  updated: string /* to trigger setState to detect change and re-render */;
}

export const mapSt2110ViewModel = (response: St2110Response, t: TFunction): St2110ViewModel => ({
  ...response.info,
  stats: { ...response.stats },
  inputFormat: response.info.inputFormat || '0',
  numOfChannels: response.info.numOfChannels || 2,
  packetTime: response.info.packetTime || 1,
  packetTimeActual: response.info.packetTimeActual,

  stateSortable: st2110StateToSortable(response.stats.state, response.stats.streamSync),
  addressTxt: `${response.info.address}:${response.info.port}`,
  typeTxt: st2110TypeToString(response.info.type, t),

  status: st2110StateToStatus(response.stats.state, response.stats.streamSync), // required for ActionBar
  selected: false,
  hidden: false,
  updated: new Date().toISOString(),
});

export const mapSt2110sResponse = (response: GetAllSt2110sResponse, t: TFunction): St2110ViewModel[] => {
  const mapped = response?.data.map((item) => mapSt2110ViewModel(item, t));
  return mapped;
};

export const newSt2110ViewModel = (): St2110ViewModel => ({
  address: '0.0.0.0',
  id: -1,
  autoStart: true,
  inputChannel: 0,
  inputFormat: '0',
  name: '',
  numOfChannels: 2,
  output: 1,
  packetTime: 1,
  port: 1,
  rtpPayload: 96,
  sourceAddress: '',
  state: St2110State.RUNNING,
  stateString: 'STARTED',
  type: St2110Type.video,

  stateSortable: St2110State.STOPPED,
  addressTxt: '',
  typeTxt: '',
  status: 'INACTIVE',
  selected: false,
  hidden: false,
  updated: new Date().toISOString(),
});

export interface ResolutionViewModel {
  value: string;
  label: string;
}

export interface ResolutionResponse extends GenericResponse {
  value: string;
  name: string;
}

export interface GetAllResolutionsResponse {
  resolutions: ResolutionResponse[];
}

const mapResolutionViewModel = (response: ResolutionResponse): ResolutionViewModel => {
  return { value: response.value, label: response.name };
};

export const mapResolutionResponse = (response: GetAllResolutionsResponse): ResolutionViewModel[] => {
  return response.resolutions?.map((item) => mapResolutionViewModel(item));
};

export const st2110ValidationSchema = (t: TFunction) =>
  yup.object<St2110ViewModel>().shape({
    name: yup.string().max(63, t('validation.maxLength', { length: 63 })),
    address: ipOrHostname(t)
      .required()
      .test('not-0.0.0.0', t('validation.invalid0dot0'), (value) => {
        if (value === '0.0.0.0') {
          return false;
        }
        return true;
      }),
    port: port(t).required(t('validation.required')),
    rtpPayload: yup
      .number()
      .typeError(t('validation.number'))
      .min(0, t('validation.st2110.rtpPayload'))
      .max(127, t('validation.st2110.rtpPayload')),
    sourceAddress: yup
      .string()
      .test('is-ipv4', t('validation.ipv4'), (value) => isIpv4OrEmpty(value))
      .test('is-ipv4-not-multicast', t('validation.sourceAddressCannotBeMulticast'), (value) =>
        isNotIpv4Multicast(value),
      ),
    tos: yup
      .number()
      .typeError(t('validation.stream.tos'))
      .min(0, t('validation.stream.tos'))
      .max(255, t('validation.stream.tos')),
    ttl: yup
      .number()
      .typeError(t('validation.stream.ttl'))
      .min(1, t('validation.stream.ttl'))
      .max(255, t('validation.stream.ttl')),
  });
